import { useQuery } from '@apollo/react-hooks';
import { type ApolloError } from '@apollo/react-hooks';
import { GET_STUDENT_INFO } from './CHECK_LIST.gql';
import {
  type FundingPlan,
  MappingType,
  programmeOptions,
} from '../../../../Nomination/components';
import { getOptionsLabel } from '../../../../Monitor/components';
import { type DeclarationValues } from '../../../../Nomination/graphql/NominationForm';
import { type SupervisorFormData } from '../../../../Monitor/graphql';
import type { SelectOption, FileData } from '../../../../../components';
import { PAGE_TEXT } from '../../../../Signup/components/lang';
import { PAGE_TEXT as FUNDING_TEXT } from '../../../../Nomination/components/lang';
import type {
  MeritAssessmentsType,
  PublicationAssessmentsType,
} from '../../../DRCAssessment';
import {
  FUNDING_STATUS_DISPLAY_STRING,
  SUCCESSFUL_FUNDING_STATUS,
} from '../../../../ScholarshipManagement/constants/fundingStatus';
import { formatDateString } from '../../../../../utils';

const VISA_TEXT = Object(PAGE_TEXT.OPTIONS_LABEL.IDENTITY_TYPE);

type PlainFundingPlan = {
  accountCode: string;
  from?: string | null;
  to?: string | null;
};

type FundingPlanArrange = {
  fundingLabel: string;
  plainFundingPlan: PlainFundingPlan[];
};

export type StudentInfo = {
  applicationNo: string;
  applicantName: string;
  visaType: string;
  isLocal: string;
  doneApplicationDeclaration: boolean;
  identityDocumentType: string;
  semester: string;
  programmeOffered: string;
  supPreviousSuccessfulSupervision: string;
  department: string;
  chiefSupervisor: string;
  coSupervisors: string[];
  justificationForFour: string;
  programmeCode: string;
  offerType: string;
  fundingSource: string[];
  supervisorRemarkToDorpgs: string;
  proofOfScheme: FileData[];
  conditionJustification: string;
  successfulFundingPlan: FundingPlanArrange[];
  declarations: DeclarationValues[];
  supervisors: SupervisorFormData[];
};

type GetStudentInfo = {
  studentInfo: StudentInfo;
  publicationAssessments: PublicationAssessmentsType[];
  meritAssessments: MeritAssessmentsType[];
  loading: boolean;
  error: ApolloError | undefined;
};

const getTransformedFundingPlan = (plan: FundingPlan[]): PlainFundingPlan[] => {
  const transformedFundingPlan: PlainFundingPlan[] = (plan ?? []).map(
    (plan: FundingPlan) => ({
      accountCode: plan.accountCode,
      from: formatDateString(plan.period.from, false),
      to: formatDateString(plan.period.to, false),
    }),
  );
  return transformedFundingPlan;
};

export const useGetStudentInfo = (
  applicationId: string,
  department: string,
): GetStudentInfo => {
  const { data, loading, error } = useQuery(GET_STUDENT_INFO, {
    variables: {
      applicationId,
      programmeCodeFilter: { department },
      nominationFilter: { application: { _id: { eq: applicationId } } },
      assessmentFilter: {
        applicationId,
      },
    },
    fetchPolicy: 'network-only',
    skip: !applicationId || !department,
  });
  const fundingSourceValues = FUNDING_TEXT.FUNDING_SOURCE.FUNDING_SCHEME.VALUES;

  const departments: SelectOption[] =
    data?.programmeChoiceMetadata?.departments ?? [];

  let studentInfo: StudentInfo = {
    applicationNo: '',
    applicantName: '',
    visaType: '',
    isLocal: '',
    doneApplicationDeclaration: false,
    identityDocumentType: '',
    semester: '',
    chiefSupervisor: '',
    coSupervisors: [],
    programmeOffered: '',
    supPreviousSuccessfulSupervision: '',
    department: '',
    supervisorRemarkToDorpgs: '',
    justificationForFour: '',
    programmeCode: '',
    offerType: '',
    fundingSource: [],
    proofOfScheme: [],
    conditionJustification: '',
    successfulFundingPlan: [],
    declarations: [],
    supervisors: [],
  };

  if (!loading) {
    const nomination = data?.nominations?.[0] ?? {};

    const {
      application,
      admission,
      fundings,
      supervisors,
      declarations,
      remarks,
    } = nomination;
    const {
      applicationNo = '',
      applicant = {},
      programmeChoice = {},
      merits = [],
      publications = [],
      declaration = {},
    } = application;
    const {
      givenName = '',
      surname = '',
      chineseName = '',
    } = applicant?.name ?? {};
    const {
      isLocal,
      identityType,
      identityDocumentType: rawIdentityDocumentType = '',
    } = applicant?.identity ?? {};
    const doneApplicationDeclaration = Boolean(declaration);
    const applicantName = `${surname?.toUpperCase()} ${givenName}${chineseName !== '' && chineseName !== null ? `(${chineseName})` : ''}`;
    const identityDocStr = (rawIdentityDocumentType as string).replaceAll(
      '_',
      ' ',
    );
    const lowCaseDocStr = identityDocStr?.toLowerCase();
    const identityDocumentType =
      ' (' +
      lowCaseDocStr?.[0]?.toUpperCase() +
      lowCaseDocStr?.substring(1) +
      ')';

    const programmeOffered = getOptionsLabel(
      programmeOptions,
      admission?.programme ?? '',
    );
    const conditionJustification = admission?.requirements?.justification ?? '';

    const justificationForFour =
      admission?.requirements?.generalEntranceRequirements?.[0]
        ?.justification ?? '';

    const semester = programmeChoice?.entryYear ?? '';
    const funding = fundings?.[0] ?? {};

    // display all fundings with status
    const fundingSource: string[] = (fundings ?? [])?.map((funding: any) => {
      const displayFundingFullName =
        fundingSourceValues[
          funding?.source as keyof typeof fundingSourceValues
        ];

      const displayedSourceStatus =
        FUNDING_STATUS_DISPLAY_STRING[
          funding?.status as keyof typeof FUNDING_STATUS_DISPLAY_STRING
        ] ?? 'N/A';
      return `${displayFundingFullName}: ${displayedSourceStatus}`;
    });

    // 1. regard Non-competitive and Other funding as Successful, of which the funding status is null
    // 2. regard Competitive funding in SUCCESSFUL_FUNDING_STATUS as Successful
    const successfulFundings = (fundings ?? [])?.filter(
      (f: any) =>
        SUCCESSFUL_FUNDING_STATUS.includes(f.status) ||
        [MappingType.NON_COMPETITIVE, MappingType.OTHER].includes(f.schema),
    );

    const successfulFundingPlan: FundingPlanArrange[] = successfulFundings.map(
      (sf: any) => {
        return {
          fundingLabel:
            fundingSourceValues[sf?.source as keyof typeof fundingSourceValues],
          plainFundingPlan: getTransformedFundingPlan(sf?.fundingPlan),
        };
      },
    );

    const visaType = VISA_TEXT[identityType ?? VISA_TEXT.OTHER_VISA];
    const departmentValue = getOptionsLabel(
      departments,
      programmeChoice?.department,
    );
    const supervisorsName: string[] = (supervisors ?? []).map(
      (sup: any) => sup.surname + ' ' + sup.givenName,
    );

    const chiefSupervisorName = supervisorsName.shift() ?? 'NA';

    studentInfo = {
      applicationNo,
      applicantName,
      visaType,
      isLocal: isLocal === true ? 'Yes' : 'No',
      doneApplicationDeclaration,
      identityDocumentType,
      semester,
      programmeCode: data?.programmeCode?.programmeCode ?? 'N/A',
      department: departmentValue,
      chiefSupervisor: chiefSupervisorName,
      coSupervisors: supervisorsName,
      justificationForFour,
      programmeOffered,
      supPreviousSuccessfulSupervision:
        supervisors?.[0]?.previousSuccessfulSupervision === true ? 'Yes' : 'No',
      offerType: '',
      fundingSource,
      conditionJustification,
      supervisorRemarkToDorpgs: remarks?.[0] ?? ' - ',
      proofOfScheme: (funding?.proofOfScheme ?? []) as FileData[],
      successfulFundingPlan,
      declarations,
      supervisors: (supervisors ?? []) as SupervisorFormData[],
    };

    const publicationIds: string[] =
      publications?.map((item: any) => item?._id) ?? [];
    const meritIds: string[] = merits?.map((item: any) => item?._id) ?? [];

    const publicationAssessments: PublicationAssessmentsType[] = (
      data?.publicationAssessment ?? []
    ).filter((pub: PublicationAssessmentsType) =>
      publicationIds.includes(pub.publicationReferenceId),
    );

    const meritAssessments: MeritAssessmentsType[] = (
      data?.meritAssessment ?? []
    ).filter((merit: MeritAssessmentsType) =>
      meritIds.includes(merit.meritReferenceId),
    );

    return {
      studentInfo,
      meritAssessments,
      publicationAssessments,
      loading,
      error,
    };
  }

  return {
    studentInfo,
    meritAssessments: [],
    publicationAssessments: [],
    loading,
    error,
  };
};
